import React from 'react'

/*
# Example usage

Note that you always need a wrapper element around the content you want to be collapsible;
this allows the expanded height to be correctly calculated.

export default function Page() {
    const [isOpen, setIsOpen] = React.useState(false)
    const { collapsibleWrapperProps, collapsibleRef } =
        useExpandCollapse<HTMLDivElement>({
            isOpen,
        })

    function toggleCollapsible() {
        setIsOpen(!isOpen)
    }

    return (
        <div>
            <button
                type="button"
                className="font-bold"
                onClick={toggleCollapsible}
            >
                Our Policies
            </button>
            <div {...collapsibleWrapperProps}>
                <div ref={collapsibleRef}>
                    90-day returns. Free shipping for orders over $50!
                </div>
            </div>
        </div>
    )
}
*/

type UseExpandCollapseReturn<E extends Element> = {
    // add these props to your wrapper element that goes around your collapsible content
    collapsibleWrapperProps: {
        style: React.CSSProperties
        className: string
    }
    // add this to your collapsible content
    collapsibleRef: React.RefObject<E | null>
}

/**
 * This hook returns styles that can be used to create an expandable/collapsible element.
 */
export function useExpandCollapse<E extends HTMLElement>({
    isOpen,
    collapsedHeight = '0px',
    animationClasses = 'duration-300 ease-out',
}: {
    isOpen: boolean
    /**
     * The height to set when the element is collapsed (make sure to include units).
     * Defaults to '0px', but you can set it to a different value if you only
     * want to partially collapse the element.
     */
    collapsedHeight?: string
    animationClasses?: string
}): UseExpandCollapseReturn<E> {
    const [expandedHeight, setExpandedHeight] = React.useState<
        string | undefined
    >()
    const collapsibleRef = React.useRef<E>(null)

    const collapsibleElem = collapsibleRef.current
    React.useLayoutEffect(() => {
        if (expandedHeight == null && collapsibleElem) {
            setExpandedHeight(
                collapsibleElem.getBoundingClientRect().height + 'px'
            )
        }
    }, [collapsibleElem, isOpen, expandedHeight, collapsedHeight])

    const height = isOpen ? expandedHeight : collapsedHeight

    const collapsibleWrapperProps = {
        style: {
            height,
            transitionProperty: 'height',
            overflow: 'hidden',
        },
        className: animationClasses,
    }

    return { collapsibleWrapperProps, collapsibleRef }
}
